import {track} from '@getmoss/moss-analytics'
import {useCallback, useMemo} from 'react'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {CATEGORY_OPTIONS} from '../components/constants'

// ─── Step name map ────────────────────────────────────────────────────────────

export const EPG_STEP_NAMES = ['company_details', 'tone', 'categories'] as const
export type EpgStepName = (typeof EPG_STEP_NAMES)[number]

export const getStepName = (step: number): EpgStepName => EPG_STEP_NAMES[step] ?? ('categories' as EpgStepName)

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePolicyGeneratorTracking() {
  const domainCountry = useGetDomainCountry()

  // Mirrors the structure from usePolicyGeneratorTracking in moss-frontend:
  // page.type = event name (queried by Metabase dashboards)
  // interaction.location = 'sg_flow' (matches existing dashboard filters)
  const trackPageLoad = useCallback(
    (type: string, details?: Record<string, unknown>) => {
      track({event: 'page_load', page: {domainCountry, type, details}})
    },
    [domainCountry],
  )

  const trackClick = useCallback(
    (type: string, element: string, details?: Record<string, unknown>) => {
      track({
        event: 'interaction',
        page: {domainCountry, type, details},
        interaction: {type: 'click', element, location: 'sg_flow'},
      })
    },
    [domainCountry],
  )

  // ─── Flow / Navigation ─────────────────────────────────────────────────────

  const trackFlowStarted = useCallback(() => {
    trackPageLoad('sg_flow_started', {
      referrer: (typeof document !== 'undefined' && document.referrer) || undefined,
    })
  }, [trackPageLoad])

  const trackStepViewed = useCallback(
    (step: number) => {
      const stepName = getStepName(step)
      trackPageLoad('sg_flow_step_viewed', {step_number: step, step_name: stepName})
    },
    [trackPageLoad],
  )

  const trackStepCompleted = useCallback(
    (step: number, value?: string) => {
      const stepName = getStepName(step)

      trackClick('sg_flow_step_completed', stepName, {
        step_number: step,
        step_name: stepName,
        ...(value !== undefined && {value}),
      })
    },
    [trackClick],
  )

  const trackStepBack = useCallback(
    (fromStep: number, toStep: number) => {
      trackClick('sg_flow_step_back', 'back_button', {
        from_step: getStepName(fromStep),
        to_step: getStepName(toStep),
      })
    },
    [trackClick],
  )

  // ─── Categories ────────────────────────────────────────────────────────────

  const trackCategoriesConfirmed = useCallback(
    (categories: string[]) => {
      const groups = CATEGORY_OPTIONS.filter((g) => g.categories.some((c) => categories.includes(c.value))).map(
        (g) => ({
          group: g.section,
          categories: g.categories.filter((c) => categories.includes(c.value)).map((c) => c.value),
        }),
      )

      trackClick('sg_categories_confirmed', 'generate_policy_button', {
        total_count: categories.length,
        group_count: groups.length,
        groups,
      })
    },
    [trackClick],
  )

  const trackEditorViewed = useCallback(
    (categoryCount: number) => {
      trackPageLoad('sg_editor_viewed', {category_count: categoryCount})
    },
    [trackPageLoad],
  )

  // ─── AI Generation ─────────────────────────────────────────────────────────

  const trackAiGenerationStarted = useCallback(
    (categories: string[]) => {
      trackPageLoad('sg_ai_generation_started', {
        categories,
        count: categories.length,
        trigger: 'manual_per_category',
      })
    },
    [trackPageLoad],
  )

  const trackAiGenerationFailed = useCallback(
    (failedCategories: string[]) => {
      trackPageLoad('sg_ai_generation_failed', {
        categories: failedCategories,
        error_type: 'api_error',
      })
    },
    [trackPageLoad],
  )

  // ─── Refine / Edit ─────────────────────────────────────────────────────────

  const trackCategoryEdited = useCallback(
    (category: string, charCount: number) => {
      trackClick('sg_category_edited', category, {
        category,
        mode: 'ai',
        has_ai_content: true,
        char_count: charCount,
      })
    },
    [trackClick],
  )

  // ─── Download ──────────────────────────────────────────────────────────────

  const trackFinishClicked = useCallback(
    (categoriesCount: number, categoriesWithContent: number) => {
      trackClick('sg_finish_clicked', 'download_policy_button', {
        categories_count: categoriesCount,
        categories_with_content: categoriesWithContent,
      })
    },
    [trackClick],
  )

  const trackDownloadDialogViewed = useCallback(() => {
    trackClick('sg_download_dialog_viewed', 'download_policy_button')
  }, [trackClick])

  const trackDownloadDialogDismissed = useCallback(() => {
    trackClick('sg_download_dialog_dismissed', 'dialog_close')
  }, [trackClick])

  const trackDownloadDialogSubmitClicked = useCallback(() => {
    trackClick('sg_download_dialog_download_clicked', 'download_pdf_button')
  }, [trackClick])

  const trackEmailSubmitted = useCallback(
    (email: string) => {
      const emailDomain = email.trim().split('@')[1] ?? ''
      trackClick('sg_email_submitted', 'email_input', {email_domain: emailDomain})
    },
    [trackClick],
  )

  const trackEmailRejected = useCallback(
    (reason: string) => {
      trackPageLoad('sg_email_rejected', {reason})
    },
    [trackPageLoad],
  )

  const trackPdfDownloaded = useCallback(
    (categoriesCount: number) => {
      trackPageLoad('sg_pdf_downloaded', {categories_count: categoriesCount})
    },
    [trackPageLoad],
  )

  return useMemo(
    () => ({
      trackFlowStarted,
      trackStepViewed,
      trackStepCompleted,
      trackStepBack,
      trackCategoriesConfirmed,
      trackEditorViewed,
      trackAiGenerationStarted,
      trackAiGenerationFailed,
      trackCategoryEdited,
      trackFinishClicked,
      trackDownloadDialogViewed,
      trackDownloadDialogDismissed,
      trackDownloadDialogSubmitClicked,
      trackEmailSubmitted,
      trackEmailRejected,
      trackPdfDownloaded,
    }),
    [
      trackFlowStarted,
      trackStepViewed,
      trackStepCompleted,
      trackStepBack,
      trackCategoriesConfirmed,
      trackEditorViewed,
      trackAiGenerationStarted,
      trackAiGenerationFailed,
      trackCategoryEdited,
      trackFinishClicked,
      trackDownloadDialogViewed,
      trackDownloadDialogDismissed,
      trackDownloadDialogSubmitClicked,
      trackEmailSubmitted,
      trackEmailRejected,
      trackPdfDownloaded,
    ],
  )
}
